import { useForm, FormProvider } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import styles from './styles.module.scss'
import { SubmitButton } from '@components/ui/Button'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { AddressFormWrapper } from './AddressFormWrapper'
import { AddressData } from '../config'
import { useAuth } from '@api/accounts/auth/hooks/useAuth'
import { ErrorField } from './shared/InputField/InputErrorMessage'
import { SubmitButtonState } from '@components/ui/Button/SubmitButton'
import { addressUpdate } from '../api/addressUpdate'
import { addressDelete } from '../api/addressDelete'
import { getErrorMessage } from '@api/graphqlShopify'
import {
  AddressFormFields,
  UpdateAddressData,
  UpdateAddressSchema
} from './shared/AddressFormFields'

export type DrawerStateProps = AddressData & {
  defaultAddress: boolean
  territoryCode: string
}

export function UpdateAddress({
  drawerState,
  handleCloseAccountDetailsDrawer
}: {
  drawerState: {
    data: DrawerStateProps
  }
  handleCloseAccountDetailsDrawer: () => void
}) {
  const { refreshUserProfile, makeRequestWithRefreshCheck } = useAuth()
  const [isProvincesLoading, setIsProvincesLoading] = useState(false)
  const [buttonState, setButtonState] = useState<SubmitButtonState>('disabled')
  const methods = useForm<UpdateAddressData & { customError?: string }>({
    mode: 'onChange',
    resolver: zodResolver(UpdateAddressSchema),
    defaultValues: {
      ...drawerState.data
    }
  })

  const formValues = methods.getValues()
  const formErrors = methods.formState.errors

  useEffect(() => {
    methods.register('territoryCode', { required: true })
  }, [methods, formValues.territoryCode])

  useEffect(() => {
    const isDisabled =
      isProvincesLoading ||
      (!methods.formState.isDirty && methods.formState.isValid)
    setButtonState(isDisabled ? 'disabled' : 'ready')
  }, [isProvincesLoading, methods.formState.isValid, methods.formState.isDirty])

  if (!drawerState.data) {
    handleCloseAccountDetailsDrawer()
    return null
  }

  const handleUpdateAddress = async (data: DrawerStateProps) => {
    setButtonState('loading')
    try {
      await makeRequestWithRefreshCheck(addressUpdate, data)
      await refreshUserProfile()

      setTimeout(() => {
        setButtonState('ready')
        handleCloseAccountDetailsDrawer()
      }, 2000)
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'

      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
      setButtonState('ready')
    }
  }

  const handleDeleteAddress = async (id: string) => {
    try {
      await makeRequestWithRefreshCheck(addressDelete, id)
      await refreshUserProfile()
      handleCloseAccountDetailsDrawer()
    } catch (e) {
      const error = e as unknown as Error
      const errorMessage =
        getErrorMessage(error) || 'Something went wrong. Try again later.'
      methods.setError(
        'customError',
        {
          type: 'focus',
          message: errorMessage
        },
        {
          shouldFocus: true
        }
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <AddressFormWrapper title="Edit Address">
        <form onSubmit={methods.handleSubmit(handleUpdateAddress)}>
          <input
            type="hidden"
            {...methods.register('id')}
            name="id"
            value={drawerState.data.id}
          />
          <AddressFormFields
            setIsProvincesLoading={setIsProvincesLoading}
            formValues={formValues}
            formSetFieldValue={methods.setValue}
          />
          <div className={styles.AccountForm__Buttons}>
            {formErrors.customError?.message && (
              <ErrorField message={formErrors.customError.message} />
            )}
            <ButtonMinimal
              underlined={true}
              onClick={() =>
                drawerState.data.id && handleDeleteAddress(drawerState.data.id)
              }
            >
              Delete Address
            </ButtonMinimal>
            <SubmitButton
              state={buttonState}
              disabled={buttonState === 'disabled'}
              type="submit"
              fullWidth="never"
            >
              Save
            </SubmitButton>
          </div>
        </form>
      </AddressFormWrapper>
    </FormProvider>
  )
}
